import React, { useState, useEffect } from "react";
import { Bot, Sparkles, Users, Plus, Zap, Settings, X, Eye, EyeOff, Save, ArrowUpRight, Construction, Layers } from "lucide-react";
import { toast } from "react-hot-toast";
import Header from '../components/Header';
import { buildApiUrl } from "../../../services/apiConfig";
import { useNavigate } from "react-router-dom";

const API_HOST = process.env.REACT_APP_API_URL || '54.204.131.48';
const API_BASE_URL = buildApiUrl('SQL');

const CustomBuildContent = () => {
  // All the state and functions from your original CustomBuild component
  const [domains, setDomains] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  let maxDomainId = "00000";

  const [useCases, setUseCases] = useState([]);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState(null);
  let maxUseCaseId = "00000";

  // New state for tabs
  const [tabs, setTabs] = useState([]);
  const [selectedTabId, setSelectedTabId] = useState(null);
  let maxTabId = "00000";

  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  let maxAgentId = "00000";

  const [tools, setTools] = useState([]);
  const [selectedToolId, setSelectedToolId] = useState(null);
  const [toolPrompt, setToolPrompt] = useState("");
  const [additionalToolPrompt, setAdditionalToolPrompt] = useState("");

  // State for tab selection
  const [activeTab, setActiveTab] = useState('customers');

  // New state for tool configuration
  const [toolConfig, setToolConfig] = useState(null);
  const [configValue, setConfigValue] = useState("");

  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showTabModal, setShowTabModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editingTab, setEditingTab] = useState(null);
  const [userId, setUserId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id');
    setUserId(storedUserId || 'U1757535255');
  }, []);

  const [agentForm, setAgentForm] = useState({
    name: "",
    tool_id: "",
    tool_prompt: "",
    status: "inactive",
    visual_prompt: "",
    widget_id: "",
    user_id: userId,
  });

  const [tabForm, setTabForm] = useState({
    name: "",
    description: "",
    use_case_id: "",
    userId: userId,
  });

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    domain: "",
    createdBy: "",
    description: "",
  });

  useEffect(() => {
    fetchDomains();
    fetchMaxDomainId();
  }, []);

  const incrementId = (currentId) => {
    // Ensure currentId is a valid string and contains only digits
    if (!currentId || typeof currentId !== 'string' || !/^\d+$/.test(currentId)) {
      console.error("Invalid ID format:", currentId);
      // Fallback to a safe default
      return "00001";
    }

    const num = parseInt(currentId, 10) + 1;
    return num.toString().padStart(5, '0');
  };

  // All your existing functions from CustomBuild
  // fetchDomains, fetchUseCases, fetchTabs, fetchAgents, etc.
  // Updated to use new API endpoint
  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/domains`);
      const data = await response.json();
      setDomains(data.domains || []);
      console.log(domains);
    } catch (err) {
      console.error("Error loading domain data:", err);
    }
  };

  const navigateToUseCase = (idx) => {
    navigate(`/use-case/${idx}`);
  }
  // Updated to use new API endpoint
  const fetchUseCases = async (domainId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/domains/${domainId}/usecases`);
      const data = await response.json();
      const usecases = data.usecases || [];

      setUseCases(
        usecases.map((item) => ({
          id: item.id,
          name: item.name,
          domain: item.domain_name,
          description: item.description,
          createdBy: item.created_by
        }))
      );
    } catch (err) {
      console.error("Error loading use cases:", err);
    }
  };

  // New function to fetch tabs for a use case
  const fetchTabs = async (useCaseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/usecases/${useCaseId}/tabs?user_id=${userId}`);
      const data = await response.json();
      const tabsData = data.tabs || [];

      setTabs(
        tabsData.map((tab) => ({
          id: tab.id,
          name: tab.name,
          description: tab.description,
          use_case_id: tab.use_case_id,
          user_id: userId
        }))
      );
    } catch (err) {
      console.error("Error loading tabs:", err);
    }
  };

  // Updated to fetch agents for a tab instead of a use case
  const fetchAgents = async (tabId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tabs/${tabId}/agents?user_id=${userId}`);
      const data = await response.json();
      const agentsData = data.agents || [];

      const formattedAgents = agentsData.map(agent => ({
        id: agent.id,
        name: agent.name,
        tool_id: agent.tool_id,
        tool_prompt: agent.tool_prompt || "",
        visual_prompt: agent.visual_prompt || "",
        widget_id: agent.widget_id || "",
        user_id: agent.user_id || "00001",
        tool_name: agent.tool_name,
        status: agent.visual_prompt ? "active" : "inactive",
        tab_id: agent.tab_id
      }));

      setAgents(formattedAgents);
    } catch (err) {
      console.error("Error loading agents:", err);
    }
  };

  // Updated to use new API endpoint
  const fetchTools = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools`);
      const data = await response.json();
      console.log("Tools data:", data);
      setTools(data.tools || []);
    } catch (err) {
      console.error("Error loading tools:", err);
    }
  };

  // Updated to use new API endpoint
  const fetchToolConfig = async (toolId) => {
    try {
      console.log("Fetching config for tool ID:", toolId);
      const response = await fetch(`${API_BASE_URL}/api/tools/${toolId}/config`);
      const data = await response.json();
      console.log("Tool config response:", data);

      if (data.error) {
        console.error("Error fetching tool config:", data.error);
        setToolConfig(null);
        return;
      }

      const config = data.config;

      if (!config) {
        console.log("No valid config options found");
        setToolConfig(null);
        setConfigValue("");
        return;
      }

      console.log("Successfully parsed config:", config);

      // Validate the parsed config has expected structure
      if (config && (config.dropdown || config.input)) {
        setToolConfig(config);
        setConfigValue(""); // Reset config value when tool changes
      } else {
        console.log("Config doesn't have expected structure (dropdown or input)");
        setToolConfig(null);
      }
    } catch (err) {
      console.error("Error in fetchToolConfig:", err);
      setToolConfig(null);
    }
  };

  // Updated to use new API endpoint
  const fetchMaxDomainId = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/domains/max-id`);
      const data = await response.json();
      maxDomainId = data.max_id || "00000";
      console.log("Max Domain ID:", maxDomainId);
    } catch (err) {
      console.error("Error loading max domain ID:", err);
    }
  };

  // Updated to use new API endpoint
  const fetchMaxUseCaseId = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/usecases/max-id`);
      const data = await response.json();
      return data.max_id || "00000";
    } catch (err) {
      console.error("Error loading max use case ID:", err);
      return "00000";
    }
  };

  // New function to fetch max tab ID
  const fetchMaxTabId = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tabs/max-id`);
      const data = await response.json();
      return data.max_id || "00000";
    } catch (err) {
      console.error("Error loading max tab ID:", err);
      return "00000";
    }
  };

  const fetchMaxAgentId = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/max-id`);
      const data = await response.json();

      // Validate the returned max_id
      const maxId = data.max_id;
      if (!maxId || typeof maxId !== 'string' || !/^\d+$/.test(maxId)) {
        console.warn("Invalid max agent ID received:", maxId);
        return "00000"; // Return a safe default
      }

      return maxId;
    } catch (err) {
      console.error("Error loading max agent ID:", err);
      return "00000";
    }
  };

  const handleSelectDomain = (idx) => {
    setSelectedDomainId(idx);
    const domainId = domains[idx]?.id;
    if (domainId) {
      fetchUseCases(domainId);
    }
    setUseCases([]);
    setSelectedUseCaseId(null);
    setTabs([]);
    setSelectedTabId(null);
    setAgents([]);
  };

  const handleAddDomain = async () => {
    try {
      const newName = prompt("Enter new domain name");
      if (!newName) return;

      await fetchMaxDomainId();
      maxDomainId = incrementId(maxDomainId);

      const newDomain = {
        ID: String(maxDomainId).padStart(5, '0'),
        name: newName.substring(0, 255)
      };

      console.log("Domain name:", newName, "and ID:", newDomain.ID);

      const res = await fetch(`${API_BASE_URL}/table-backend/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newDomain)
      });

      if (res.ok) {
        const responseData = await res.json();
        console.log("Row inserted successfully:", responseData);
        fetchDomains();
        console.log(domains);
      } else {
        const errorData = await res.json();
        console.error("Error inserting row:", errorData);
      }
    } catch (error) {
      console.error("Failed to fetch:", error);
    }
  };

  const handleSelectUseCase = (idx) => {
    setSelectedUseCaseId(idx);
    const selected = useCases[idx];
    console.log(selected.id);
    setFormData({
      id: selected.id,
      title: selected.name,
      domain: selected.domain || "",
      createdBy: selected.createdBy || "",
      description: selected.description || "",
    });
    fetchTabs(selected.id);
    setSelectedTabId(null);
    setAgents([]);
  };

  // New function to handle tab selection
  const handleSelectTab = (idx) => {
    setSelectedTabId(idx);
    const selected = tabs[idx];
    if (selected && selected.id) {
      fetchAgents(selected.id);
    }
  };

  const handleAddUseCase = () => {
    if (selectedDomainId === null && selectedDomainId !== 0) {
      alert("Please select a domain first");
      return;
    }
    const domain = domains[selectedDomainId];
    if (!domain) {
      alert("Please select a domain first");
      return;
    }
    setUseCases([...useCases, {
      id: null,
      name: "New Use Case",
      domain: domain?.name || "",
      description: "",
      createdBy: ""
    }]);
    setTabs([]);
    setSelectedTabId(null);
    setAgents([]);
    setSelectedUseCaseId(useCases.length);
    setFormData({
      title: "New Use Case",
      domain: domain?.name || "",
      createdBy: "",
      description: "",
    });
  };

  // New function to add a tab
  const handleAddTab = () => {
    if (selectedUseCaseId === null && selectedUseCaseId !== 0) {
      alert("Please select a use case first");
      return;
    }

    const useCase = useCases[selectedUseCaseId];
    if (!useCase || !useCase.id) {
      alert("Please save the use case first");
      return;
    }

    setTabForm({
      name: "New Tab",
      description: "",
      use_case_id: useCase.id
    });

    setEditingTab(null);
    setShowTabModal(true);
  };

  const handleUseCaseSave = async () => {
    try {
      if (selectedDomainId === null && selectedDomainId !== 0) {
        alert("Please select a domain");
        return;
      }

      const newID = await fetchMaxUseCaseId();
      const newUseCaseId = incrementId(newID);
      const domain = domains[selectedDomainId];

      if (!domain) {
        alert("Domain not found");
        return;
      }

      if (!formData.id) {
        // Creating a new use case
        const newUseCase = {
          id: newUseCaseId,
          name: formData.title,
          description: formData.description,
          domain_id: domain.id,
          created_by: formData.createdBy
        };

        const res = await fetch(`${API_BASE_URL}/table-backend/use_cases`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newUseCase)
        });

        const result = await res.json();

        if (res.ok) {
          console.log("Use case saved successfully:", result);
          toast.success("Use Case Saved Successfully");

          // Refresh use cases for the current domain
          await fetchUseCases(domain.id);
          setSelectedUseCaseId(null);
        } else {
          console.error("Error saving use case:", result.error);
        }
      } else {
        // Updating an existing use case
        const res = await fetch(`${API_BASE_URL}/run-update-backend`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `UPDATE use_cases SET name='${formData.title}',description='${formData.description}',created_by='${formData.createdBy}' where id='${formData.id}'`
          })
        });

        const result = await res.json();
        console.log(result);

        if (res.ok) {
          console.log(domain.id);
          console.log("Use case saved successfully");
          toast.success("Use Case Saved Successfully");
          await fetchUseCases(domain.id);
        } else {
          toast.error("Error while saving the Use Case");
          console.error("Error saving use case:", result.error);
        }
      }
    } catch (err) {
      console.error("Error saving use case:", err);
    }
  };

  // New function to save a tab
  const handleTabSave = async () => {
    try {
      if (!tabForm.name) {
        toast.error("Tab name is required");
        return;
      }

      if (!tabForm.use_case_id) {
        toast.error("No use case selected");
        return;
      }

      if (editingTab) {
        // Update existing tab
        const updateResponse = await fetch(`${API_BASE_URL}/run-update-backend`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `UPDATE tabs SET name='${tabForm.name}', description='${tabForm.description || ""}' WHERE id='${editingTab.id}'`
          })
        });

        if (updateResponse.ok) {
          toast.success("Tab updated successfully");
          fetchTabs(tabForm.use_case_id);
        } else {
          toast.error("Error updating tab");
          console.error("Error updating tab:", await updateResponse.json());
        }
      } else {
        // Create new tab
        const newTabId = incrementId(await fetchMaxTabId());

        const createResponse = await fetch(`${API_BASE_URL}/table-backend/tabs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: newTabId,
            name: tabForm.name,
            use_case_id: tabForm.use_case_id,
            user_id: userId,
          })
        });

        if (createResponse.ok) {
          toast.success("Tab created successfully");
          fetchTabs(tabForm.use_case_id);
        } else {
          toast.error("Error creating tab");
          console.error("Error creating tab:", await createResponse.json());
        }
      }

      closeTabModal();
    } catch (error) {
      console.error("Error saving tab:", error);
      toast.error("Error saving tab");
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    setUseCases((prev) => prev.map((uc, idx) =>
      idx === selectedUseCaseId
        ? { ...uc, [field === "title" ? "name" : field]: value }
        : uc
    ));
  };

  const openAgentModal = async (agent = null) => {
    await fetchTools();
    console.log(agent);

    if (agent) {
      // We're editing an existing agent
      setToolPrompt(agent.tool_prompt || "");
      setAdditionalToolPrompt(agent.visual_prompt || "");
      setSelectedToolId(agent.tool_id || null);
      setAgentForm({
        name: agent.name,
        tool_id: agent.tool_id || "",
        tool_prompt: agent.tool_prompt || "",
        status: agent.status || "inactive",
        visual_prompt: agent.visual_prompt || "",
        widget_id: agent.widget_id || "",
        user_id: agent.user_id || "00001",
      });
      setEditingAgent(agent);

      // Fetch tool config for the selected tool
      if (agent.tool_id) {
        await fetchToolConfig(agent.tool_id);
      }
    } else {
      // We're adding a new agent
      setToolPrompt("");
      setAdditionalToolPrompt("");
      setSelectedToolId(null);
      setToolConfig(null);
      setConfigValue("");
      setAgentForm({
        name: "",
        tool_id: "",
        tool_prompt: "",
        status: "inactive",
        visual_prompt: "",
        widget_id: "",
        user_id: "00001",
      });
      setEditingAgent(null);
    }

    setShowAgentModal(true);
  };

  const closeAgentModal = () => {
    setShowAgentModal(false);
    setEditingAgent(null);
    setToolConfig(null);
    setConfigValue("");
  };

  // New function to open tab modal
  const openTabModal = (tab = null) => {
    if (tab) {
      setTabForm({
        name: tab.name,
        description: tab.description || "",
        use_case_id: tab.use_case_id
      });
      setEditingTab(tab);
    } else {
      // Make sure we have a use case selected
      if (selectedUseCaseId === null && selectedUseCaseId !== 0) {
        toast.error("Please select a use case first");
        return;
      }

      const useCase = useCases[selectedUseCaseId];
      if (!useCase || !useCase.id) {
        toast.error("Please save the use case first");
        return;
      }

      setTabForm({
        name: "New Tab",
        description: "",
        use_case_id: useCase.id
      });
      setEditingTab(null);
    }

    setShowTabModal(true);
  };

  // New function to close tab modal
  const closeTabModal = () => {
    setShowTabModal(false);
    setEditingTab(null);
  };

  const handleAgentFormChange = (field, value) => {
    setAgentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // New function to handle tab form changes
  const handleTabFormChange = (field, value) => {
    setTabForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Updated tool selection handler
  const handleToolSelection = async (toolId) => {
    setSelectedToolId(toolId);
    handleAgentFormChange("tool_id", toolId);

    // Fetch configuration for the selected tool
    await fetchToolConfig(toolId);
  };

  // Function to render tool configuration input
  const renderToolConfigInput = () => {
    console.log("Rendering tool config input, toolConfig:", toolConfig);

    if (!toolConfig) {
      console.log("No toolConfig, returning null");
      return null;
    }

    if (toolConfig.dropdown) {
      console.log("Rendering dropdown with config:", toolConfig.dropdown);
      return (
        <div className="form-field">
          <label className="text-black font-medium">{toolConfig.dropdown.label}</label>
          <select
            value={configValue}
            onChange={(e) => setConfigValue(e.target.value)}
            className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black-500"
          >
            <option value="">Select an option</option>
            {toolConfig.dropdown.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
          {configValue && (
            <div className="space-y-2 mt-2">
              <div>
                <label className="text-black font-medium">URL</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black-500"
                  placeholder="Enter Database URL"
                />
              </div>
              <div>
                <label className="text-black font-medium">Username</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black-500"
                  placeholder="Enter Database Username"
                />
              </div>
              <div>
                <label className="text-black font-medium">Password</label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black-500"
                  placeholder="Enter Database Password"
                />
              </div>
            </div>
          )}
        </div>
      );
    } else if (toolConfig.input) {
      console.log("Rendering input with config:", toolConfig.input);
      return (
        <div className="form-field">
          <label>{toolConfig.input.label}</label>
          <input
            type={toolConfig.input.type || "text"}
            value={configValue}
            onChange={(e) => setConfigValue(e.target.value)}
            placeholder={toolConfig.input.placeholder || ""}
          />
        </div>
      );
    }

    console.log("No matching config type found");
    return null;
  };

  const generateUniqueAgentID = () => {
    // Get current timestamp in milliseconds
    const timestamp = Date.now();

    // Convert to string and take the last 5 characters
    // This will give us the last 5 digits of the timestamp
    const id = timestamp.toString().slice(-5);

    // Add a random letter prefix to ensure it's 5 characters and more unique
    const randomLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];

    return randomLetter + id.slice(-4);
  };

  const saveAgent = async () => {
    try {
      if (!selectedTabId && selectedTabId !== 0) {
        alert("Please select a tab first");
        return;
      }

      const currentTab = tabs[selectedTabId];
      if (!currentTab || !currentTab.id) {
        alert("Please save the tab first");
        return;
      }
      console.log("Current tab:", currentTab);
      const agentData = {
        name: agentForm.name,
        tool_id: selectedToolId || null,
        tool_prompt: toolPrompt,
        visual_prompt: agentForm.status === "active" ? additionalToolPrompt : "",
        user_id: userId,
        tab_id: currentTab.id // Link to tab instead of use case
      }

      if (editingAgent) {
        // Update existing agent
        const updateResponse = await fetch(`${API_BASE_URL}/run-update-backend`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `UPDATE widgets SET name='${agentData.name}', tool_id='${agentData.tool_id}',tool_prompt='${agentData.tool_prompt}',visual_prompt='${agentData.visual_prompt}', user_id='${agentData.user_id}', tab_id='${agentData.tab_id}' where id='${editingAgent.id}'`
          })
        });

        let updateRes = await updateResponse.json();

        if (updateResponse.ok) {
          console.log("Agent updated successfully");
          toast.success("Agent updated successfully")
          fetchAgents(currentTab.id);
        } else {
          toast.error("Error Updating Agent");
          console.error("Error updating agent:", updateRes.error);
        }
      } else {
        // Create new agent
        const newAgentId = generateUniqueAgentID();
        console.log("New Agent ID:", newAgentId);

        const createResponse = await fetch(`${API_BASE_URL}/table-backend/widgets`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...agentData, id: newAgentId })
        });

        if (createResponse.ok) {
          console.log("Agent created successfully");
          toast.success("Agent created successfully");
          fetchAgents(currentTab.id);
        } else {
          toast.error("Error creating agent");
          console.error("Error creating agent:", await createResponse.json());
        }
      }

      closeAgentModal();
    } catch (error) {
      console.error("Error saving agent:", error);
      toast.error("Error saving agent");
    }
  };

  return (

    <div className="p-6">
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
        {/* Domains Panel */}
        <div className="col-span-1 lg:col-span-4 bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> Domains
            </h3>
            <button
              onClick={handleAddDomain}
              className="p-2 bg-primary hover:bg-primaryDark text-white rounded-lg transition-colors group shadow-sm"
            >
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
            {domains.map((domain, idx) => (
              <div
                key={domain.id}
                onClick={() => handleSelectDomain(idx)}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border ${selectedDomainId === idx
                  ? 'bg-primary text-white shadow-lg shadow-primary/25 border-primary'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200 hover:border-primaryLight'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{domain.name}</h4>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="p-1 text-inherit hover:text-primaryDark transition-colors"
                    aria-label="Go to domain"
                  >
                    <ArrowUpRight size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {domains.length === 0 && (
              <p className="text-gray-500 text-center py-8">No domains available</p>
            )}
          </div>
        </div>

        {/* Use Cases Panel */}
        <div className="col-span-1 lg:col-span-4 bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" /> Use Cases
            </h3>
            <button
              disabled={selectedDomainId === null}
              onClick={handleAddUseCase}
              className="p-2 bg-primary hover:bg-primaryDark disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors group shadow-sm"
            >
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
            {useCases.map((useCase, idx) => (
              <div
                key={useCase.id}
                onClick={() => handleSelectUseCase(idx)}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border ${selectedUseCaseId === idx
                  ? 'bg-primary text-white shadow-lg shadow-primary/25 border-primary'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200 hover:border-primaryLight'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{useCase.name}</h4>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToUseCase(useCase.id);
                    }}
                    className="p-1 text-inherit hover:text-primaryDark transition-colors"
                    aria-label="Go to use case"

                  >
                    <ArrowUpRight size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {useCases.length === 0 && (
              <p className="text-gray-500 text-center py-8">Select a domain to view use cases</p>
            )}
          </div>
        </div>

        {/* Details Panel */}
        <div className="col-span-1 lg:col-span-4 bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
          {selectedUseCaseId !== null ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                  <Construction className="w-5 h-5 text-primary" /> {formData.title || "Untitled Use Case"}
                </h3>
                <button
                  onClick={handleUseCaseSave}
                  className="px-4 py-2 bg-primary hover:bg-primaryDark text-white rounded-md transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Title</label>
                  <input
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter title..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Created By</label>
                  <input
                    value={formData.createdBy}
                    onChange={(e) => handleChange("createdBy", e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter creator..."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 font-medium">Description</label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Enter description..."
                />
              </div>

              {/* Tabs Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-700 font-medium flex items-center gap-2">
                    <Layers className="w-5 h-5 text-primary" /> Tabs
                  </label>
                  <button
                    onClick={handleAddTab}
                    className="px-4 py-2 bg-primary hover:bg-primaryDark text-white rounded-md transition-colors flex items-center gap-2"
                    disabled={!formData.id}
                  >
                    <Plus className="w-4 h-4" /> Add Tab
                  </button>
                </div>
                <div className="space-y-3 max-h-[200px] overflow-y-auto">
                  {tabs.map((tab, idx) => (
                    <div
                      key={tab.id}
                      onClick={() => handleSelectTab(idx)}
                      className={`flex items-center justify-between p-4 bg-white rounded-lg border transition-shadow ${selectedTabId === idx
                        ? 'border-primary shadow-md'
                        : 'border-gray-200 shadow-sm hover:shadow-md'
                        }`}
                    >
                      <div>
                        <h4 className="text-gray-800 font-medium">{tab.name}</h4>
                        {tab.description && (
                          <p className="text-gray-500 text-sm">{tab.description}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openTabModal(tab);
                        }}
                        className="p-2 bg-primary hover:bg-primaryDark rounded-md transition-colors"
                      >
                        <Settings className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                  {tabs.length === 0 && (
                    <p className="text-gray-500 text-center py-8">
                      {formData.id ? "No tabs created yet" : "Save the use case to add tabs"}
                    </p>
                  )}
                </div>
              </div>

              {/* Agents Section - Only show when a tab is selected */}
              {selectedTabId !== null && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-gray-700 font-medium flex items-center gap-2">
                      <Bot className="w-5 h-5 text-primary" /> Agents for {tabs[selectedTabId]?.name}
                    </label>
                    <button
                      onClick={() => openAgentModal(null)}
                      className="px-4 py-2 bg-primary hover:bg-primaryDark text-white rounded-md transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Add Agent
                    </button>
                  </div>
                  <div className="space-y-3 max-h-[calc(100vh-550px)] overflow-y-auto">
                    {agents.map((agent) => (
                      <div
                        key={agent.id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-3 h-3 rounded-full ${agent.status === 'active' ? 'bg-green-400' : 'bg-gray-400'
                              }`}
                          />
                          <div>
                            <h4 className="text-gray-800 font-medium">{agent.name}</h4>
                            <p className="text-gray-500 text-sm">{agent.tool_name || "No tool selected"}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => openAgentModal(agent)}
                          className="p-2 bg-primary hover:bg-primaryDark rounded-md transition-colors"
                        >
                          <Settings className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ))}
                    {agents.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No agents created yet</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <Bot className="w-24 h-24 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Select a use case from the left to see details</p>
            </div>
          )}
        </div>
      </div>

      {/* Agent Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-primary">
                {editingAgent ? "Edit Agent" : "Add Agent"}
              </h3>
              <button
                onClick={closeAgentModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-gray-700 font-medium">Agent Name</label>
                <input
                  value={agentForm.name}
                  onChange={(e) => handleAgentFormChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter agent name..."
                />
              </div>
              {tools.length > 0 && (
                <div className="space-y-4">
                  <label className="text-gray-700 font-medium">Select Tool</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tools.map((tool) => (
                      <label
                        key={tool.id}
                        className={`p-3 border-2 rounded-md cursor-pointer transition-all ${selectedToolId === tool.id
                          ? 'border-primary bg-primaryLight/20'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          value={tool.id}
                          checked={selectedToolId === tool.id}
                          onChange={() => handleToolSelection(tool.id)}
                          className="sr-only"
                        />
                        <div className="text-sm font-medium text-gray-800">{tool.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{tool.category}</div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {renderToolConfigInput()}
              {selectedToolId && (
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Tool Prompt</label>
                  <textarea
                    rows={4}
                    value={toolPrompt}
                    onChange={(e) => {
                      setToolPrompt(e.target.value);
                      handleAgentFormChange("tool_prompt", e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="Enter tool-specific instructions..."
                  ></textarea>
                </div>
              )}
              {selectedToolId && selectedToolId !== "00002" && (
                <div className="space-y-4">
                  <label className="text-gray-700 font-medium">Visualization</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="active"
                        checked={agentForm.status === "active"}
                        onChange={(e) => handleAgentFormChange("status", e.target.value)}
                        className="text-primary focus:ring-primary"
                      />
                      <Eye className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="inactive"
                        checked={agentForm.status === "inactive"}
                        onChange={(e) => handleAgentFormChange("status", e.target.value)}
                        className="text-primary focus:ring-primary"
                      />
                      <EyeOff className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700">Inactive</span>
                    </label>
                  </div>
                </div>
              )}
              {selectedToolId && selectedToolId !== "00002" && agentForm.status === "active" && (
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Visualization Prompt</label>
                  <textarea
                    rows={4}
                    value={additionalToolPrompt}
                    onChange={(e) => {
                      setAdditionalToolPrompt(e.target.value);
                      handleAgentFormChange("visual_prompt", e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="Enter visualization instructions..."
                  />
                </div>
              )}
              <div className="flex items-center justify-between pt-6">
                <button
                  onClick={saveAgent}
                  disabled={!agentForm.name || !selectedToolId}
                  className="px-6 py-2 bg-primary hover:bg-primaryDark disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Save className="w-4 h-4" /> Save Agent
                </button>
                <button
                  onClick={closeAgentModal}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Modal */}
      {showTabModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium text-primary">
                {editingTab ? "Edit Tab" : "Add Tab"}
              </h3>
              <button
                onClick={closeTabModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-gray-700 font-medium">Tab Name</label>
                <input
                  value={tabForm.name}
                  onChange={(e) => handleTabFormChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter tab name..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-700 font-medium">Description</label>
                <textarea
                  rows={4}
                  value={tabForm.description}
                  onChange={(e) => handleTabFormChange("description", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Enter tab description..."
                />
              </div>
              <div className="flex items-center justify-between pt-6">
                <button
                  onClick={handleTabSave}
                  disabled={!tabForm.name}
                  className="px-6 py-2 bg-primary hover:bg-primaryDark disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Save className="w-4 h-4" /> Save Tab
                </button>
                <button
                  onClick={closeTabModal}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomBuildContent;